import { type ChangeEvent, type FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, IconButton, Typography } from "@mui/material";
import { CloudAdd, Danger } from "iconsax-react";
import { Delete } from "react-iconly";
import ConfirmationDialog from "../../../../../components/ConfirmationDialog";
import CustomTextField from "../../../../../components/Fields/Textfield";
import useNewSymbolDialogStyles from "./useNewSymbolDialogStyles";

// Must stay in sync with the backend's AllowedIconExtensions/MaxIconSizeBytes
// (TechnicalEstimateLegendService.cs) — both sides enforce the same limits independently.
const ALLOWED_ICON_EXTENSIONS = [".jpg", ".jpeg", ".png", ".ico"];
const MAX_ICON_SIZE_MB = 2;

export interface UploadedSymbolDraft {
  id: string;
  file: File;
  previewUrl: string;
  description: string;
  vermogenscode: string;
}

interface NewSymbolDialogProps {
  open: boolean;
  onSave: (drafts: UploadedSymbolDraft[]) => Promise<void>;
  onClose: () => void;
}

const createDraftId = () => `draft-${Math.random().toString(36).slice(2, 10)}`;

const NewSymbolDialog: FC<NewSymbolDialogProps> = ({ open, onSave, onClose }) => {
  const { t } = useTranslation();
  const styles = useNewSymbolDialogStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [drafts, setDrafts] = useState<UploadedSymbolDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const handleUploadClick = () => fileInputRef.current?.click();

  const validateFile = (file: File): string | null => {
    const name = file.name.toLowerCase();
    const validType = ALLOWED_ICON_EXTENSIONS.some((ext) => name.endsWith(ext));
    if (!validType) {
      return t("configuration.ingenieurslegende.invalidIconType", {
        allowed: "JPG, PNG, ICO",
      });
    }
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_ICON_SIZE_MB) {
      return t("configuration.ingenieurslegende.iconTooLarge", {
        size: sizeMB.toFixed(2),
        max: MAX_ICON_SIZE_MB,
      });
    }
    return null;
  };

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setFileError(null);
    const newDrafts: UploadedSymbolDraft[] = [];
    for (const file of Array.from(files)) {
      const error = validateFile(file);
      if (error) {
        setFileError(error);
        continue;
      }
      newDrafts.push({
        id: createDraftId(),
        file,
        previewUrl: URL.createObjectURL(file),
        description: "",
        vermogenscode: "ALG",
      });
    }
    setDrafts((prev) => [...prev, ...newDrafts]);
    event.target.value = "";
  };

  const handleRemoveDraft = (id: string) => {
    setDrafts((prev) => prev.filter((draft) => draft.id !== id));
  };

  const handleDescriptionChange = (id: string, description: string) => {
    setDrafts((prev) => prev.map((draft) => (draft.id === id ? { ...draft, description } : draft)));
  };

  const handleVermogenscodeChange = (id: string, vermogenscode: string) => {
    setDrafts((prev) => prev.map((draft) => (draft.id === id ? { ...draft, vermogenscode } : draft)));
  };

  const handleDiscard = () => {
    setDrafts([]);
    onClose();
  };

  const handleSave = async () => {
    if (drafts.length === 0 || saving) return;
    setSaving(true);
    try {
      await onSave(drafts);
      setDrafts([]);
    } catch {
      // onSave surfaces its own error feedback — keep drafts so the user can retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfirmationDialog
      open={open}
      onCancel={handleDiscard}
      onConfirm={handleSave}
      title={t("configuration.ingenieurslegende.newSymbolDialogTitle")}
      showIcon={false}
      showCloseButton
      maxWidth="md"
      confirmFirst
      confirmText={saving ? t("common.saving") : t("common.save")}
      cancelText={t("common.delete")}
      confirmColor="inherit"
      confirmButtonProps={{ disabled: drafts.length === 0 || saving }}
    >
      <Box sx={styles.body}>
        <Box sx={styles.headerRow}>
          <Box sx={styles.counterWrap}>
            <Typography variant="subtitle2" sx={styles.counterText}>
              <Box component="span" sx={styles.counterNumber}>
                {drafts.length}{" "}
              </Box>
              {t("configuration.ingenieurslegende.uploadedSymbols")}
            </Typography>
            <Typography variant="caption" sx={styles.captionText}>
              {t("configuration.ingenieurslegende.uploadMultipleHint")}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={handleUploadClick}
            startIcon={<CloudAdd size={18} variant="Bold" color={styles.colors.upload} />}
            sx={styles.uploadButton}
          >
            {t("configuration.ingenieurslegende.uploadSymbolButton")}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_ICON_EXTENSIONS.join(",")}
            multiple
            hidden
            onChange={handleFilesSelected}
          />
        </Box>

        {fileError && (
          <Alert severity="error" onClose={() => setFileError(null)}>
            {fileError}
          </Alert>
        )}

        {drafts.length === 0 ? (
          <Box sx={styles.emptyStateRow}>
            <Box sx={styles.emptyStateIconBox}>
              <Danger size={24} variant="Bold" color={styles.colors.warning} />
            </Box>
            <Typography variant="body2" sx={styles.emptyStateText}>
              {t("configuration.ingenieurslegende.noSymbolDetected")}
            </Typography>
          </Box>
        ) : (
          drafts.map((draft) => (
            <Box key={draft.id} sx={styles.rowCard}>
              <Box component="img" src={draft.previewUrl} alt="" sx={styles.rowImage} />
              <CustomTextField
                mode="light"
                fullWidth
                label={t("configuration.ingenieurslegende.descriptionLabel")}
                value={draft.description}
                onChange={(e) => handleDescriptionChange(draft.id, e.target.value)}
                sx={styles.descriptionField}
              />
              {/* <CustomTextField
                mode="light"
                label="Vermogenscode*"
                value={draft.vermogenscode}
                onChange={(e) => handleVermogenscodeChange(draft.id, e.target.value)}
                sx={styles.codeField}
              /> */}
              <IconButton size="small" onClick={() => handleRemoveDraft(draft.id)} sx={styles.rowDeleteButton}>
                <Delete set="curved" stroke="light" primaryColor={styles.colors.delete} style={styles.deleteIcon} />
              </IconButton>
            </Box>
          ))
        )}
      </Box>
    </ConfirmationDialog>
  );
};

export default NewSymbolDialog;
